import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, Settings, Star, MessageCircle, ChevronRight, Send, Check, Trash2, Heart, Users, Package, Plus, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { API, CIGAR_PLACEHOLDER } from '../config/constants';
import BottomNav from '../components/BottomNav';
import useModalHistory from '../hooks/useModalHistory';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [activityFeed, setActivityFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Modal state for clickable stats
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [modalData, setModalData] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [statsError, setStatsError] = useState(false);
  
  // Handle browser back button for modal
  const { closeModal } = useModalHistory(modalOpen, () => setModalOpen(false), 'admin-modal');

  const fetchStats = useCallback(async () => {
    try {
      setStatsError(false);
      const res = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000 // 30 second timeout for slow connections
      });
      setStats(res.data);
    } catch (error) {
      console.error('Failed to fetch admin stats:', error);
      setStatsError(true);
      // Don't navigate away - show error state instead
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/profile');
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      });
      setUsers(res.data);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  }, [token]);

  const fetchFeedback = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users/feedback`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      });
      setFeedback(res.data);
    } catch (error) {
      console.error('Failed to fetch feedback');
    }
  }, [token]);

  const fetchActivityFeed = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/activity-feed`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActivityFeed(res.data);
    } catch (error) {
      console.error('Failed to fetch activity feed');
    }
  }, [token]);

  const markFeedbackRead = async (feedbackId) => {
    try {
      await axios.put(`${API}/users/feedback/${feedbackId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeedback(prev => prev.map(f => f.id === feedbackId ? { ...f, read: true } : f));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const deleteFeedback = async (feedbackId) => {
    try {
      await axios.delete(`${API}/users/feedback/${feedbackId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeedback(prev => prev.filter(f => f.id !== feedbackId));
      toast.success('Feedback deleted');
    } catch (error) {
      toast.error('Failed to delete feedback');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'bug': return 'text-red-400 bg-red-400/20';
      case 'feature': return 'text-blue-400 bg-blue-400/20';
      case 'cigar': return 'text-amber-400 bg-amber-400/20';
      default: return 'text-white/70 bg-white/10';
    }
  };

  useEffect(() => {
    if (token) {
      fetchStats();
      fetchUsers();
      fetchFeedback();
      fetchActivityFeed();
    }
  }, [token, fetchStats, fetchUsers, fetchFeedback, fetchActivityFeed]);

  // Handle stat card click
  const handleStatClick = async (type) => {
    setModalType(type);
    setModalOpen(true);
    setModalLoading(true);
    
    try {
      let endpoint = '';
      switch (type) {
        case 'cigars':
          endpoint = '/admin/all-cigars';
          break;
        case 'users':
          endpoint = '/admin/users';
          break;
        case 'ratings':
          endpoint = '/admin/all-ratings';
          break;
        case 'comments':
          endpoint = '/admin/all-comments';
          break;
        case 'favorites':
          endpoint = '/admin/all-favorites';
          break;
        default:
          return;
      }
      
      const res = await axios.get(`${API}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModalData(res.data);
    } catch (error) {
      toast.error(`Failed to fetch ${type}`);
      closeModal();
    } finally {
      setModalLoading(false);
    }
  };

  const getModalTitle = () => {
    switch (modalType) {
      case 'cigars': return 'All Cigars';
      case 'users': return 'All Users';
      case 'ratings': return 'All Ratings';
      case 'comments': return 'All Comments';
      case 'favorites': return 'All Favorites';
      default: return '';
    }
  };

  const getModalIcon = () => {
    switch (modalType) {
      case 'cigars': return <Package className="text-amber-500" size={24} />;
      case 'users': return <Users className="text-amber-500" size={24} />;
      case 'ratings': return <Star className="text-amber-500" size={24} />;
      case 'comments': return <MessageCircle className="text-amber-500" size={24} />;
      case 'favorites': return <Heart className="text-amber-500" size={24} />;
      default: return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0B0C] flex items-center justify-center">
        <div className="text-white/50">Loading dashboard...</div>
      </div>
    );
  }

  if (statsError && !stats) {
    return (
      <div className="min-h-screen bg-[#0B0B0C] flex flex-col items-center justify-center gap-4">
        <div className="text-white/50">Failed to load dashboard data</div>
        <button 
          onClick={() => {
            setLoading(true);
            fetchStats();
            fetchUsers();
            fetchFeedback();
            fetchActivityFeed();
          }}
          className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-2 rounded-lg"
        >
          Retry
        </button>
        <button 
          onClick={() => navigate('/profile')}
          className="text-white/50 hover:text-white"
        >
          Back to Profile
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0C] pb-24">
      {/* Header */}
      <div className="flex items-center p-4 border-b border-white/10">
        <button onClick={() => navigate('/profile')} className="text-white mr-4">
          <X size={24} />
        </button>
        <h1 className="playfair text-xl text-white flex-1 text-center pr-8 flex items-center justify-center gap-2">
          <Settings className="text-amber-500" size={24} />
          Admin Dashboard
        </h1>
      </div>

      {/* Stats Cards */}
      <div className="p-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
        <button 
          onClick={() => handleStatClick('cigars')}
          className="bg-gradient-to-br from-amber-900/30 to-amber-700/20 border border-amber-500/30 rounded-xl p-4 text-center hover:from-amber-900/50 hover:to-amber-700/30 transition-all"
        >
          <p className="text-3xl font-bold text-amber-500">{stats?.total_cigars || 0}</p>
          <p className="text-white/50 text-sm">Total Cigars</p>
        </button>
        <button 
          onClick={() => handleStatClick('users')}
          className="bg-white/5 border border-white/10 rounded-xl p-4 text-center hover:bg-white/10 transition-all"
        >
          <p className="text-3xl font-bold text-white">{stats?.total_users || 0}</p>
          <p className="text-white/50 text-sm">Total Users</p>
        </button>
        <button 
          onClick={() => handleStatClick('ratings')}
          className="bg-white/5 border border-white/10 rounded-xl p-4 text-center hover:bg-white/10 transition-all"
        >
          <p className="text-3xl font-bold text-white">{stats?.total_ratings || 0}</p>
          <p className="text-white/50 text-sm">Total Ratings</p>
        </button>
        <button 
          onClick={() => navigate('/admin/analytics')}
          className="bg-gradient-to-br from-purple-900/30 to-purple-700/20 border border-purple-500/30 rounded-xl p-4 text-center hover:from-purple-900/50 hover:to-purple-700/30 transition-all"
        >
          <BarChart3 size={28} className="text-purple-400 mx-auto mb-1" />
          <p className="text-white/50 text-sm">Analytics</p>
        </button>
        <button 
          onClick={() => handleStatClick('comments')}
          className="bg-white/5 border border-white/10 rounded-xl p-4 text-center hover:bg-white/10 transition-all"
        >
          <p className="text-3xl font-bold text-white">{stats?.total_comments || 0}</p>
          <p className="text-white/50 text-sm">Total Comments</p>
        </button>
        <button 
          onClick={() => handleStatClick('favorites')}
          className="bg-white/5 border border-white/10 rounded-xl p-4 text-center hover:bg-white/10 transition-all"
        >
          <p className="text-3xl font-bold text-white">{stats?.total_favorites || 0}</p>
          <p className="text-white/50 text-sm">Total Favorites</p>
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-white/10 mx-4">
        <button
          onClick={() => setActiveTab('overview')}
          className={`flex-1 py-3 text-sm font-medium ${
            activeTab === 'overview' ? 'text-amber-500 border-b-2 border-amber-500' : 'text-white/50'
          }`}
        >
          Recent Activity
        </button>
        <button
          onClick={() => setActiveTab('feedback')}
          className={`flex-1 py-3 text-sm font-medium relative ${
            activeTab === 'feedback' ? 'text-amber-500 border-b-2 border-amber-500' : 'text-white/50'
          }`}
        >
          Feedback
          {feedback.filter(f => !f.read).length > 0 && (
            <span className="absolute -top-1 right-2 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full min-w-[18px]">
              {feedback.filter(f => !f.read).length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('users')}
          className={`flex-1 py-3 text-sm font-medium ${
            activeTab === 'users' ? 'text-amber-500 border-b-2 border-amber-500' : 'text-white/50'
          }`}
        >
          Users ({users.length})
        </button>
      </div>

      {/* Tab Content */}
      <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 350px)' }}>
        {activeTab === 'overview' && (
          <div className="space-y-3">
            <h3 className="text-white font-bold mb-3">Recent Activity (Last 30 days)</h3>
            {activityFeed.length === 0 ? (
              <p className="text-white/40 text-sm">No recent activity</p>
            ) : (
              activityFeed.map((activity) => (
                <button
                  key={`${activity.type}-${activity.id}`}
                  onClick={() => activity.cigar_id && navigate(`/cigar/${activity.cigar_id}`)}
                  className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 text-left transition-colors"
                >
                  <div className="flex items-start gap-3">
                    {/* Activity Icon */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      activity.type === 'rating' ? 'bg-amber-500/20' :
                      activity.type === 'comment' ? 'bg-blue-500/20' :
                      activity.type === 'reply' ? 'bg-blue-500/20' :
                      activity.type === 'comment_deleted' ? 'bg-red-500/20' :
                      activity.type === 'cigar_added' ? 'bg-green-500/20' :
                      activity.type === 'cigar_deleted' ? 'bg-red-500/20' :
                      activity.type === 'favorite' ? 'bg-pink-500/20' :
                      activity.type === 'feedback' ? 'bg-purple-500/20' :
                      'bg-white/10'
                    }`}>
                      {activity.type === 'rating' && <Star size={14} className="text-amber-500" />}
                      {activity.type === 'comment' && <MessageCircle size={14} className="text-blue-500" />}
                      {activity.type === 'reply' && <MessageCircle size={14} className="text-blue-500" />}
                      {activity.type === 'comment_deleted' && <Trash2 size={14} className="text-red-500" />}
                      {activity.type === 'cigar_added' && <Plus size={14} className="text-green-500" />}
                      {activity.type === 'cigar_deleted' && <Trash2 size={14} className="text-red-500" />}
                      {activity.type === 'favorite' && <Heart size={14} className="text-pink-500" />}
                      {activity.type === 'feedback' && <Send size={14} className="text-purple-500" />}
                    </div>
                    
                    {/* Activity Content */}
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm">
                        <span className="text-amber-500 font-medium">{activity.username}</span>
                        {' '}
                        <span className="text-white/70">{activity.description}</span>
                      </p>
                      <p className="text-white/40 text-xs mt-1">{formatDate(activity.created_at)}</p>
                    </div>
                    
                    {/* Rating badge for ratings */}
                    {activity.type === 'rating' && (
                      <div className="flex items-center gap-1 bg-amber-500/20 px-2 py-1 rounded flex-shrink-0">
                        <Star size={12} className="text-amber-500 fill-amber-500" />
                        <span className="text-amber-500 font-bold text-sm">{activity.rating}</span>
                      </div>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <div className="space-y-2">
            {users.map((u) => (
              <button
                key={u.id}
                onClick={() => navigate(`/user/${u.id}`)}
                className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 flex items-center gap-3 transition-colors text-left"
              >
                {u.profile_pic ? (
                  <img src={u.profile_pic} alt={u.username} className="w-12 h-12 rounded-full object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center text-white font-bold text-lg">
                    {u.username?.charAt(0).toUpperCase() || 'U'}
                  </div>
                )}
                <div className="flex-1">
                  <p className="text-white font-medium">{u.username}</p>
                  <p className="text-white/40 text-xs">{u.email}</p>
                </div>
                <div className="text-right text-xs">
                  <p className="text-amber-500">{u.ratings_count} ratings</p>
                  <p className="text-white/50">{u.comments_count} comments</p>
                  <p className="text-white/40">{u.cigars_added} added</p>
                </div>
                <ChevronRight size={20} className="text-white/30" />
              </button>
            ))}
          </div>
        )}

        {activeTab === 'feedback' && (
          <div className="space-y-3">
            {feedback.length === 0 ? (
              <div className="text-center py-8">
                <Send className="mx-auto text-white/20 mb-2" size={32} />
                <p className="text-white/50">No feedback yet</p>
              </div>
            ) : (
              feedback.map((item) => (
                <div 
                  key={item.id} 
                  className={`bg-white/5 rounded-lg p-4 border ${item.read ? 'border-white/10' : 'border-amber-500/50'}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-amber-500 font-medium">{item.username}</p>
                      <p className="text-white/40 text-xs">{item.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getCategoryColor(item.category)}`}>
                        {item.category}
                      </span>
                      {!item.read && (
                        <span className="bg-amber-500 text-black text-xs px-2 py-0.5 rounded-full font-medium">
                          NEW
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-white text-sm mb-2">{item.message}</p>
                  <div className="flex items-center justify-between">
                    <p className="text-white/40 text-xs">{formatDate(item.created_at)}</p>
                    <div className="flex items-center gap-2">
                      {!item.read && (
                        <button
                          onClick={() => markFeedbackRead(item.id)}
                          className="p-1.5 bg-green-600/20 hover:bg-green-600/30 rounded text-green-400"
                          title="Mark as read"
                        >
                          <Check size={16} />
                        </button>
                      )}
                      <button
                        onClick={() => deleteFeedback(item.id)}
                        className="p-1.5 bg-red-600/20 hover:bg-red-600/30 rounded text-red-400"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Data Modal */}
      {modalOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setModalOpen(false); }}
        >
          <div className="bg-[#141416] rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                {getModalIcon()}
                <h2 className="text-white text-lg font-bold">{getModalTitle()}</h2>
                <span className="text-white/50 text-sm">({modalData.length})</span>
              </div>
              <button onClick={() => setModalOpen(false)} className="text-white/50 hover:text-white p-2">
                <X size={24} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {modalLoading ? (
                <div className="text-center py-8 text-white/50">Loading...</div>
              ) : modalData.length === 0 ? (
                <div className="text-center py-8 text-white/50">No data found</div>
              ) : (
                <div className="space-y-2">
                  {/* Cigars List */}
                  {modalType === 'cigars' && modalData.map((cigar) => (
                    <button
                      key={cigar.id}
                      onClick={() => { setModalOpen(false); navigate(`/cigar/${cigar.id}`); }}
                      className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 flex items-center gap-3 text-left transition-colors"
                    >
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-white/5 flex-shrink-0">
                        <img src={cigar.image_url || CIGAR_PLACEHOLDER} alt={cigar.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-amber-500 text-xs">{cigar.brand}</p>
                        <p className="text-white font-medium truncate">{cigar.name}</p>
                        <p className="text-white/40 text-xs">{cigar.strength} · {cigar.origin}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Star size={14} className="text-amber-500" />
                        <span className="text-amber-500 text-sm">{cigar.average_rating?.toFixed(1) || '0.0'}</span>
                      </div>
                      <ChevronRight size={18} className="text-white/30" />
                    </button>
                  ))}

                  {/* Users List */}
                  {modalType === 'users' && modalData.map((user) => (
                    <button
                      key={user.id}
                      onClick={() => { setModalOpen(false); navigate(`/user/${user.id}`); }}
                      className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 flex items-center gap-3 text-left transition-colors"
                    >
                      {user.profile_pic ? (
                        <img src={user.profile_pic} alt={user.username} className="w-12 h-12 rounded-full object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center text-white font-bold">
                          {user.username?.charAt(0).toUpperCase() || 'U'}
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="text-white font-medium">{user.username}</p>
                        <p className="text-white/40 text-xs">{user.email}</p>
                      </div>
                      <div className="text-right text-xs">
                        <p className="text-amber-500">{user.ratings_count || 0} ratings</p>
                        <p className="text-white/50">{user.comments_count || 0} comments</p>
                      </div>
                      <ChevronRight size={18} className="text-white/30" />
                    </button>
                  ))}

                  {/* Ratings List */}
                  {modalType === 'ratings' && modalData.map((rating) => (
                    <button
                      key={rating.id}
                      onClick={() => { setModalOpen(false); navigate(`/cigar/${rating.cigar_id}`); }}
                      className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 text-left transition-colors"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-amber-500 font-medium">{rating.username}</span>
                        <div className="flex items-center gap-1 bg-amber-500/20 px-2 py-0.5 rounded">
                          <Star size={12} className="text-amber-500 fill-amber-500" />
                          <span className="text-amber-500 font-bold text-sm">{rating.rating}</span>
                        </div>
                      </div>
                      <p className="text-white text-sm">{rating.cigar_brand} {rating.cigar_name}</p>
                      <p className="text-white/40 text-xs mt-1">{formatDate(rating.created_at)}</p>
                    </button>
                  ))}

                  {/* Comments List */}
                  {modalType === 'comments' && modalData.map((comment) => (
                    <button
                      key={comment.id}
                      onClick={() => { setModalOpen(false); navigate(`/cigar/${comment.cigar_id}`); }}
                      className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 text-left transition-colors"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-amber-500 font-medium">{comment.username}</span>
                        <span className="text-white/40 text-xs">{formatDate(comment.created_at)}</span>
                      </div>
                      <p className="text-white/50 text-xs mb-1">{comment.cigar_brand} {comment.cigar_name}</p>
                      <p className="text-white text-sm line-clamp-2">{comment.text}</p>
                    </button>
                  ))}

                  {/* Favorites List */}
                  {modalType === 'favorites' && modalData.map((favorite) => (
                    <button
                      key={favorite.id}
                      onClick={() => { setModalOpen(false); navigate(`/cigar/${favorite.cigar_id}`); }}
                      className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 flex items-center gap-3 text-left transition-colors"
                    >
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-white/5 flex-shrink-0">
                        <img src={favorite.cigar_image || CIGAR_PLACEHOLDER} alt={favorite.cigar_name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-amber-500 text-xs">{favorite.username}</p>
                        <p className="text-white font-medium truncate">{favorite.cigar_brand} {favorite.cigar_name}</p>
                        <p className="text-white/40 text-xs">{formatDate(favorite.created_at)}</p>
                      </div>
                      <Heart size={18} className="text-red-500 fill-red-500" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default AdminDashboard;
